import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../../common/axiosInstans.ts';
// @ts-ignore
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CircularProgress from '@mui/material/CircularProgress';
import { PersonAdd } from '@mui/icons-material';
import OrganizationService from '../superAdmin/TenantOrganizations/OrganizationService.ts';
import { useGlobalStore } from '../../application/store/store.tsx';
import { CreateUserRequest } from '../../application/types/requests/createUserRequest.ts';

const CreateUser = () => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        userName: '',
        barcode: '',
        organizationIds: [],
    });
    const [errors, setErrors] = useState<Record<string, string[]>>({});
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const { setMessage, clearUserCache } = useGlobalStore();

    const organizationService = new OrganizationService();
    const [organizationList, setOrganizationList] = useState([]);

    useEffect(() => {
        const fetchOrganizations = async () => {
            try {
                const data = await organizationService.getOrganizations();

                const simplifiedOrgs = data.map((org) => ({
                    id: org.id,
                    name: org.name,
                }));

                setOrganizationList(simplifiedOrgs);
            } catch (error) {
                console.error('Failed to fetch organizations:', error);
            }
        };

        fetchOrganizations();
    }, []);

    const getOrgNameById = (id) => {
        const org = organizationList.find((org) => org.id === id);
        return org ? org.name : '';
    };

    const handleOrgChange = (
        event: SelectChangeEvent<typeof formData.organizationIds>
    ) => {
        const {
            target: { value },
        } = event;

        const selectedIds =
            typeof value === 'string' ? value.split(',') : value;

        setFormData((prev) => ({
            ...prev,
            organizationIds: selectedIds,
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        if (formData.organizationIds.length === 0) {
            setMessage('Ange minst en Organisation.', 'warning');
            setIsLoading(false);
            return;
        }

        // Prepare the request data according to the new API format
        const requestData: CreateUserRequest = {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            userName: formData.userName,
            phoneNumber: null, // Always null as requested
            barcode: formData.barcode || null, // Send null if empty
            organizationIds: formData.organizationIds,
        };

        try {
            await axiosInstance.post('/users', requestData);
            // Clear user list cache so the new user appears in the list
            clearUserCache();
            navigate('..');
        } catch (error) {
            if (error?.response?.data?.errors) {
                setErrors(error.response.data.errors);
            }
            if (error?.response?.data?.messages) {
                setErrors({
                    CreateUserRequestValidator: error.response.data.messages,
                });
            } else {
                setErrors({
                    General: [
                        'Ett oväntat fel uppstod. Är användarnamnet eller Eposten redan registrerat?',
                    ],
                });
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('..');
    };

    return (
        <div>
            <Box display="flex" alignItems="center" mb={2}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleCancel}
                    color="primary"
                >
                    Tillbaka
                </Button>
            </Box>
            <Container maxWidth="sm">
                <Paper
                    elevation={3}
                    className="p-8 border-r-6"
                    component="form"
                    onSubmit={handleSubmit}
                >
                    <Typography variant="h4" align="center" gutterBottom>
                        Skapa ny användare
                    </Typography>
                    {Object.keys(errors).length > 0 && (
                        <Box mt={2} mb={2}>
                            {Object.values(errors).map((errorMessages, index) =>
                                errorMessages.map((message, idx) => (
                                    <Alert
                                        key={`${index}-${idx}`}
                                        severity="error"
                                        sx={{ mb: 1 }}
                                    >
                                        {message}
                                    </Alert>
                                ))
                            )}
                        </Box>
                    )}
                    <Grid container spacing={2}>
                        {[
                            { label: 'Förnamn', name: 'firstName' },
                            { label: 'Efternamn', name: 'lastName' },
                            { label: 'Epost', name: 'email', type: 'email' },
                            { label: 'Användarnamn', name: 'userName' },
                            {
                                label: 'Streckkod',
                                name: 'barcode',
                                required: false,
                            },
                        ].map(
                            ({
                                label,
                                name,
                                type = 'text',
                                required = true,
                            }) => (
                                <Grid item xs={12} key={name}>
                                    <TextField
                                        fullWidth
                                        label={label}
                                        name={name}
                                        type={type}
                                        value={formData[name]}
                                        onChange={handleChange}
                                        required={required}
                                        variant="outlined"
                                    />
                                </Grid>
                            )
                        )}
                        <div>
                            <FormControl
                                sx={{ marginTop: 2, marginLeft: 2, width: 488 }}
                            >
                                <InputLabel>Tillhör Organisationer</InputLabel>
                                <Select
                                    multiple
                                    value={formData.organizationIds}
                                    onChange={handleOrgChange}
                                    input={
                                        <OutlinedInput label="Tillhör Organisationer" />
                                    }
                                    renderValue={(selected) => (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: 0.5,
                                            }}
                                        >
                                            {selected.map((id) => (
                                                <Chip
                                                    key={id}
                                                    label={getOrgNameById(id)}
                                                />
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {organizationList.map((org) => (
                                        <MenuItem key={org.id} value={org.id}>
                                            {org.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                    </Grid>

                    <Button
                        startIcon={
                            isLoading ? (
                                <CircularProgress size={24} color="info" />
                            ) : (
                                <PersonAdd />
                            )
                        }
                        type="submit"
                        variant="contained"
                        fullWidth
                        sx={{
                            mt: 2,
                            bgcolor: 'primary.main',
                            color: 'white',
                            '&:hover': { bgcolor: 'primary.dark' },
                        }}
                    >
                        Skapa användare
                    </Button>
                </Paper>
            </Container>
        </div>
    );
};

export default CreateUser;
